import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useContext, useState } from 'react';
import { useToastNotification, useLocalizeMessage } from 'libs.nucleus';
import { OrgContext } from '../../contexts/org';
import { SiteContext } from '../../contexts/site';
import { getApiInstance, getApiErrorMessage } from '../../helpers/api.helpers';
import { useCurrentEnvironment } from '../use_current_environment';
export const useFetchParticipants = ({ initialPage = 0, initialPageSize = 10 }) => {
    const { environment } = useCurrentEnvironment();
    const { site } = useContext(SiteContext);
    const { participantStatusMap } = useContext(OrgContext);
    const { addNotification } = useToastNotification();
    const translate = useLocalizeMessage();
    const [totalPages, setTotalPages] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [pagination, setPagination] = useState({ pageIndex: initialPage, pageSize: initialPageSize });
    const [searchValue, setSearchValue] = useState(undefined);
    const fetchParticipants = async (pageSize, pageIndex, searchValue) => {
        if (!environment) {
            return [];
        }
        try {
            const { data: response } = await getApiInstance(environment).get(`/v2/routes/site_subjects/${site?._id}`, {
                params: {
                    enablePagination: true,
                    sort: JSON.stringify({ created: -1 }),
                    where: JSON.stringify({
                        c_type: { $in: ['participant', null] },
                        //  Handle search value as exact match. If you need fuzzy logic maybe use: $regex: searchValue, $options: 'i'
                        ...(searchValue ? { c_number: { $eq: searchValue } } : {}),
                    }),
                    limit: pageSize,
                    skip: pageIndex * pageSize,
                },
            });
            // the response is different from other axon endpoints b/c we needed to put total number of objects in the response
            if (response?.data?.data?.length === 0) {
                setTotalPages(0);
                setTotalItems(0);
                return [];
            }
            setTotalItems(response?.data?.total || 0);
            setTotalPages(Math.ceil((response?.data?.total || 0) / pagination.pageSize));
            const participantsData = response?.data?.data || [];
            const participantsWithLabels = participantsData.map((participant) => ({
                ...participant,
                c_status_label: participantStatusMap.get(participant.c_status) || participant.c_status,
            }));
            return participantsWithLabels;
        }
        catch (error) {
            const message = getApiErrorMessage(error);
            addNotification({
                title: '',
                subtitle: message ?? translate('error.generic'),
                type: 'error',
            });
            return [];
        }
    };
    const { data: participants, isLoading, isFetching, } = useQuery({
        queryKey: ['participantsList', environment?.id, site?._id, { pagination, searchValue }],
        queryFn: () => fetchParticipants(pagination.pageSize, pagination.pageIndex, searchValue),
        enabled: true,
        placeholderData: keepPreviousData,
    });
    return {
        isLoading,
        isFetching,
        participants,
        totalPages,
        totalItems,
        pagination,
        setPagination,
        setSearchValue,
    };
};
